import { useState } from "react";
import styled from "@emotion/styled";
import { GrButton, GrMenu, GrMenuItem } from "@jnpmedi/grebob";
import timezone from "dayjs/plugin/timezone";
import { DocsTable } from "@newComponents/common/DocsTable/DocsTable";
import { InformationGuide } from "@newComponents/layout/InformationGuide";
import { handleDownloadUrlResponse } from "@newLibs/converting";
import { MvnMessage } from "@newLibs/utils";
import { useFolderContext } from "@newHooks/context/folder/folder.context";
import { notify } from "@newHooks/context/snackbar/notify";
import { useDownloadFolderFileMutation } from "@newHooks/mutation/folderFile/downloadFolderFile.mutation";
import { useBrowseFoldersQuery } from "@newHooks/query/folder/browseFolders.query";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { useArchiverBrowseFolderTable } from "./hooks/useArchiverBrowseFolderTable";
import { DOCS_DOCUMENT_LANG, DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { BrowseEntrySummary, BrowseEntryType, FolderType } from "@maven-rest/docs/Folder.dto";
import { FolderSharingRole } from "@maven-rest/docs/FolderSharing.dto";
import { isSharingRoleAuthorized } from "@maven-rest/docs/FolderSharing.lib";
import dayjs from "dayjs";
dayjs.extend(timezone);

export type ArchiverBrowseFolderTableProps = {
  organizationId: bigint;
  isDeletedResource: boolean;
  onSharingSettingsClick: (entry: BrowseEntrySummary) => void;
  onMoveToClick: (entry: BrowseEntrySummary) => void;
  onRenameClick: (entry: BrowseEntrySummary) => void;
  onDeleteFolderClick: (entry: BrowseEntrySummary) => void;
  onDeleteFileClick: (entry: BrowseEntrySummary) => void;
};

export function ArchiverBrowseFolderTable({
  organizationId,
  isDeletedResource,
  onSharingSettingsClick,
  onMoveToClick,
  onRenameClick,
  onDeleteFolderClick,
  onDeleteFileClick,
}: ArchiverBrowseFolderTableProps) {
  const { currentFolderId } = useFolderContext();

  const [contextMenuAnchor, setContextMenuAnchor] = useState<{ anchor: HTMLElement | null; row: BrowseEntrySummary } | null>(null);

  const handleOpenContextMenu = ({ anchor, row }: { anchor: HTMLElement | null; row: BrowseEntrySummary }) => {
    setContextMenuAnchor({ anchor, row });
  };

  const { columns } = useArchiverBrowseFolderTable(handleOpenContextMenu);

  const {
    data: browseFoldersData,
    isLoading: isBrowseFoldersLoading,
    error,
  } = useBrowseFoldersQuery({
    organizationId,
    folderType: FolderType.ARCHIVER,
    folderId: currentFolderId,
    includeDeletedResource: isDeletedResource,
  });

  const { mutate: downloadFileMutate } = useDownloadFolderFileMutation({
    onSuccess: (data) => {
      if (data.url) {
        handleDownloadUrlResponse(data.url);
      }

      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_DOWNLOAD_SUCC), "SUCC");
    },
    onError: (error) => {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_DOWNLOAD_FAIL, { message: error.responseMessage }), "WARN");
    },
  });

  const getContextMenuItems = (row?: BrowseEntrySummary | null): (GrMenuItem & { visibility: boolean })[] => {
    if (!row) return [];

    return [
      {
        icon: { type: "share" },
        text: "Sharing Settings",
        visibility: !row.resource.isDestroyed,
        disabled: !row.isManager,
        type: "item",
        onClick: () => onSharingSettingsClick(row),
      },
      {
        icon: { type: "arrow-bar-to-down" },
        text: "Download",
        visibility: !row.resource.isDestroyed && row.type === BrowseEntryType.Document,
        disabled: !row.role || !isSharingRoleAuthorized({ myRole: row.role, role: FolderSharingRole.Viewer }),
        type: "item",
        onClick: () => downloadFileMutate({ fileId: row.resource.id }),
      },
      {
        icon: { type: "file-arrow-right" },
        text: "Move Item",
        visibility: !row.resource.isDestroyed,
        disabled: !row.isManager || row.type === BrowseEntryType.Proposal,
        type: "item",
        onClick: () => onMoveToClick(row),
      },
      {
        icon: { type: "pencil" },
        text: "Rename",
        visibility: !row.resource.isDestroyed,
        disabled: !row.isManager,
        type: "item",
        onClick: () => onRenameClick(row),
      },

      {
        icon: { type: "trash" },
        text: "Delete",
        visibility: !row.resource.isDestroyed,
        disabled: !row.isManager,
        type: "item",
        onClick: () => {
          row.type === BrowseEntryType.Folder ? onDeleteFolderClick(row) : onDeleteFileClick(row);
        },
      },
    ];
  };

  if (error?.response?.data?.code === "BILLING_FUNCTIONALITY_NOT_SUBSCRIBED") {
    return (
      <InformationGuide
        style={{ height: "100%", backgroundColor: "inherit" }}
        title={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_ARCHIVER_SUBSCRIBE_PLAN_TITLE)}
        description={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_ARCHIVER_SUBSCRIBE_PLAN_DESCRIPTION)}
        confirmButton={
          <GrButton onClick={() => window.open(`${process.env.REACT_APP_AUTH_FE_URL}/subscription/maven-docs`)}>Subscribe a Plan</GrButton>
        }
      />
    );
  }

  return (
    <ArchiverBrowseFolderTableWrapper>
      <DocsTable>
        <DocsTable.Contents<BrowseEntrySummary>
          columns={columns}
          data={browseFoldersData?.entries as Array<BrowseEntrySummary>}
          isDataLoading={isBrowseFoldersLoading}
          emptyGuide={{ emptyType: "dragAndDrop", title: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_TABLE_EMPTY_DND_DESCRIPTION) }}
        />
      </DocsTable>
      <GrMenu
        anchorElement={contextMenuAnchor?.anchor || null}
        position={{ vertical: "bottom", horizontal: "right" }}
        items={getContextMenuItems(contextMenuAnchor?.row).filter((menu) => menu.visibility)}
        onClose={() => setContextMenuAnchor(null)}
      />
    </ArchiverBrowseFolderTableWrapper>
  );
}

const ArchiverBrowseFolderTableWrapper = styled("section")`
  height: calc(100vh - ${baseHeight.appHeaderHeight} - ${baseHeight.appContainerBreadcrumbTitleButton});
`;
